/* HMBLE PDP — gallery + variant resolution + dynamic CTA. */

(() => {
  const sections = document.querySelectorAll('.hmble-pdp');
  if (!sections.length) return;

  const fmt = (cents) => {
    const cur = window.Shopify?.currency?.active || 'EUR';
    if (cur !== 'EUR') {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur }).format(cents / 100);
      } catch (_) {}
    }
    const euros = (cents / 100).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `€${euros}`;
  };

  const initSection = (section) => {
    // --- Gallery ---
    const slides = Array.from(section.querySelectorAll('.hmble-pdp__slide'));
    const counter = section.querySelector('[data-counter]');
    let idx = 0;

    const renderCounter = () => {
      if (!counter || slides.length === 0) return;
      const i = String(idx + 1).padStart(2, '0');
      const n = String(slides.length).padStart(2, '0');
      counter.textContent = `${i} / ${n}`;
    };
    const go = (n) => {
      if (slides.length === 0) return;
      slides[idx]?.classList.remove('is-active');
      idx = (n + slides.length) % slides.length;
      slides[idx].classList.add('is-active');
      renderCounter();
    };
    section.querySelector('[data-prev]')?.addEventListener('click', () => go(idx - 1));
    section.querySelector('[data-next]')?.addEventListener('click', () => go(idx + 1));
    renderCounter();

    // --- Variant resolution ---
    const productJsonEl = section.querySelector('[data-product-json]');
    if (!productJsonEl) return;
    let product;
    try { product = JSON.parse(productJsonEl.textContent); } catch (_) { return; }

    const form = section.querySelector('.hmble-pdp__form');
    if (!form) return;

    const variantInput = form.querySelector('[data-variant-id]');
    const priceEl = section.querySelector('[data-price]');
    const ctaLabel = section.querySelector('[data-cta-label]');
    const ctaBtn = section.querySelector('[data-add]');
    const ctaLabelMobile = section.querySelector('[data-cta-label-mobile]');
    const ctaBtnMobile = section.querySelector('[data-add-mobile]');
        // Jersey personalization: the price on the button includes the name.
    const persoBox = form.querySelector('[data-perso]');
    const persoToggle = persoBox ? persoBox.querySelector('[data-perso-toggle]') : null;
    const persoCents = persoBox ? (parseInt(persoBox.dataset.addonPrice, 10) || 0) : 0;
    if (persoToggle) persoToggle.addEventListener('change', () => refresh());
        // Mobile sticky button: it shows before the size picker, so the first
    // tap without an explicit size choice scrolls to the sizes instead of
    // adding the preselected size (XS) to the cart.
    const optionPickers = form.querySelectorAll('[data-option-index]');
    let sizeConfirmed = optionPickers.length === 0;
    form.addEventListener('change', (e) => {
      if (e.target.matches('[data-option-input]')) sizeConfirmed = true;
    });
    if (ctaBtnMobile && optionPickers.length) {
      ctaBtnMobile.addEventListener('click', (e) => {
        if (sizeConfirmed) return;
        e.preventDefault();
        sizeConfirmed = true; // a second tap adds the size now on screen
        const picker = optionPickers[0];
        picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (picker.animate) {
          picker.animate(
            [{ transform: 'translateX(0)' }, { transform: 'translateX(-8px)' }, { transform: 'translateX(8px)' }, { transform: 'translateX(0)' }],
            { duration: 400, delay: 400, iterations: 2 }
          );
        }
      });
    }

    // For one-size / single-variant products the picker isn't rendered.
    // Fall back to the first available (or just first) variant's options.
    const fallbackVariant = product.variants.find((v) => v.available) || product.variants[0];
    const fallbackOptions = fallbackVariant ? fallbackVariant.options : [];

    const readOptions = () => product.options.map((_, i) => {
      const checked = form.querySelector(`[data-option-index="${i}"] [data-option-input]:checked`);
      if (checked) return checked.value;
      return fallbackOptions[i] ?? null;
    });

    const matchVariant = (opts) => product.variants.find((v) =>
      v.options.every((o, i) => o === opts[i])
    );

    const updateAvailability = () => {
      product.options.forEach((_, i) => {
        const radios = form.querySelectorAll(`[data-option-index="${i}"] [data-option-input]`);
        const current = readOptions();
        radios.forEach((radio) => {
          const candidate = current.map((v, idx) => (idx === i ? radio.value : v));
          const variant = matchVariant(candidate);
          const label = radio.closest('label');
          if (!label) return;
          const ok = variant && variant.available;
          label.classList.toggle('is-soldout', !ok);
          radio.disabled = !variant;
        });
      });
    };

    // Translations come from a JSON blob the section embeds for the JS.
    const i18n = window.__hmblePdpI18n || {
      add_to_cart: 'Add to cart',
      add_to_cart_short: 'Add',
      sold_out: 'Sold out',
      variant_unavailable: 'Variant unavailable',
    };

    const refresh = () => {
      const variant = matchVariant(readOptions());
      if (!variant) {
        ctaBtn.disabled = true;
        if (ctaBtnMobile) ctaBtnMobile.disabled = true;
        if (ctaLabel) ctaLabel.textContent = i18n.variant_unavailable;
        if (ctaLabelMobile) ctaLabelMobile.textContent = i18n.variant_unavailable;
        return;
      }
      variantInput.value = variant.id;

      // Cap the orderable quantity at the variant's available stock
      // (inventory tracked by Shopify and oversell not allowed).
      const qtyInput = section.querySelector('[data-qty-input]');
      if (qtyInput) {
        const tracked = variant.inventory_management === 'shopify'
          && variant.inventory_policy !== 'continue'
          && typeof variant.inventory_quantity === 'number';
        const cap = tracked ? Math.max(1, variant.inventory_quantity) : 99;
        qtyInput.max = cap;
        if (Number(qtyInput.value) > cap) qtyInput.value = cap;
      }

      const priceTxt = fmt(variant.price + (persoToggle && persoToggle.checked ? persoCents : 0));
      if (priceEl) priceEl.textContent = priceTxt;
      if (variant.available) {
        ctaBtn.disabled = false;
        if (ctaBtnMobile) ctaBtnMobile.disabled = false;
        if (ctaLabel) ctaLabel.innerHTML = `${i18n.add_to_cart} — <span data-cta-price>${priceTxt}</span>`;
        if (ctaLabelMobile) ctaLabelMobile.innerHTML = `${i18n.add_to_cart_short} — <span data-cta-price-mobile>${priceTxt}</span>`;
      } else {
        ctaBtn.disabled = true;
        if (ctaBtnMobile) ctaBtnMobile.disabled = true;
        if (ctaLabel) ctaLabel.textContent = i18n.sold_out;
        if (ctaLabelMobile) ctaLabelMobile.textContent = i18n.sold_out;
      }

      // Update label display
      product.options.forEach((opt, i) => {
        const fieldset = form.querySelector(`[data-option-index="${i}"]`);
        const valSpan = fieldset?.querySelector('[data-option-value]');
        if (valSpan) valSpan.textContent = variant.options[i];
      });

      // Active swatch/chip
      form.querySelectorAll('[data-option-input]').forEach((radio) => {
        radio.closest('label')?.classList.toggle('is-active', radio.checked);
      });

      // URL param
      try {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      } catch (_) {}
    };

    form.addEventListener('change', (e) => {
      if (!e.target.matches('[data-option-input]')) return;
      refresh();
      updateAvailability();
    });

    updateAvailability();
    refresh();
  };

  sections.forEach(initSection);

  // --- Quantity selector -----------------------------------------------
  document.querySelectorAll('.hmble-pdp').forEach((section) => {
    const input = section.querySelector('[data-qty-input]');
    if (!input) return;
    const decr = section.querySelector('[data-qty-decr]');
    const incr = section.querySelector('[data-qty-incr]');
    // max is kept in sync with the selected variant's stock by refresh()
    const clamp = (n) => Math.max(1, Math.min(Number(input.max) || 99, n | 0));
    const set = (n) => { input.value = clamp(n); };
    decr?.addEventListener('click', () => set(Number(input.value) - 1));
    incr?.addEventListener('click', () => set(Number(input.value) + 1));
    input.addEventListener('change', () => set(Number(input.value)));
  });

  // --- Sizing modal -----------------------------------------------------
  // One modal per size chart. Each "Size guide" button opens the modal
  // whose data-sizing-modal matches its data-sizing-open (the bundle has
  // one per item); otherwise it falls back to the first modal.
  document.querySelectorAll('.hmble-pdp').forEach((section) => {
    const modals = Array.from(section.querySelectorAll('[data-sizing-modal]'));
    if (!modals.length) return;

    const open = (modal) => {
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      // Force a paint at opacity:0 before flipping data-open, so the
      // backdrop blur and the panel slide-in animate in sync.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => modal.setAttribute('data-open', ''));
      });
    };
    const close = (modal) => {
      modal.removeAttribute('data-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      setTimeout(() => { if (!modal.hasAttribute('data-open')) modal.hidden = true; }, 300);
    };

    section.querySelectorAll('[data-sizing-open]').forEach((b) => {
      b.addEventListener('click', () => {
        const key = b.getAttribute('data-sizing-open');
        const modal = modals.find((m) => m.getAttribute('data-sizing-modal') === key) || modals[0];
        open(modal);
      });
    });
    modals.forEach((modal) => {
      modal.querySelectorAll('[data-sizing-close]').forEach((c) => c.addEventListener('click', () => close(modal)));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      modals.forEach((modal) => { if (modal.hasAttribute('data-open')) close(modal); });
    });
  });
})();

/* ── Jersey name personalization (+€5 addon product) ─────────────────
   When the toggle is on and a name is entered, intercept the native
   form submit and add BOTH the jersey (with the name as line item
   property) and the hidden addon product in one /cart/add.js call.
   Falls back to the native submit on any fetch error. */
(() => {
  document.querySelectorAll('.hmble-pdp__form').forEach((form) => {
    const perso = form.querySelector('[data-perso]');
    if (!perso) return;
    const toggle = perso.querySelector('[data-perso-toggle]');
    const nameInput = perso.querySelector('[data-perso-name]');
    const addonId = parseInt(perso.dataset.addonVariant, 10);
    if (!toggle || !nameInput || !addonId) return;

    toggle.addEventListener('change', () => {
      nameInput.hidden = !toggle.checked;
      nameInput.classList.remove('is-error');
      if (toggle.checked) nameInput.focus();
      else nameInput.value = '';
    });
    // Live-sanitize: uppercase, letters/digits/space only, max 10 chars
    nameInput.addEventListener('input', () => {
      const clean = nameInput.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '').slice(0, 10);
      if (nameInput.value !== clean) nameInput.value = clean;
      nameInput.classList.remove('is-error');
    });

    form.addEventListener('submit', (e) => {
      if (!toggle.checked) return; // native flow, no personalization
      const name = nameInput.value.trim().toUpperCase();
      if (!name || !/^[A-Z0-9 ]{1,10}$/.test(name)) {
        e.preventDefault();
        nameInput.classList.add('is-error');
        nameInput.focus();
        return;
      }
      e.preventDefault();
      const mainId = parseInt(form.querySelector('[data-variant-id]').value, 10);
      const qty = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;
      fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            { id: mainId, quantity: qty, properties: { 'Nome maglia': name } },
            { id: addonId, quantity: qty, properties: { 'Per': name } }
          ]
        })
      })
        .then((r) => { if (!r.ok) throw new Error('add failed'); window.location.href = (window.Shopify?.routes?.root || '/') + 'cart'; })
        .catch(() => form.submit());
    });
  });
})();

/* ── Cappellino in omaggio ───────────────────────────────────────────
   On products with a gift (data-gift-variant, set by the section when
   the product is tagged "omaggio-cappellino"), add the cap to the cart
   together with the product, one cap per item. The automatic "Buy X
   get Y" discount brings the cap to 0. Falls back to the native submit
   (product only) on any fetch error. */
(() => {
  document.querySelectorAll('.hmble-pdp__form').forEach((form) => {
    const gift = form.querySelector('[data-gift-variant]');
    if (!gift) return;
    const giftId = parseInt(gift.value, 10);
    if (!giftId) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const mainId = parseInt(form.querySelector('[data-variant-id]').value, 10);
      const qty = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;
      fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            { id: mainId, quantity: qty },
            { id: giftId, quantity: qty }
          ]
        })
      })
        .then((r) => { if (!r.ok) throw new Error('add failed'); window.location.href = `${root}cart`; })
        .catch(() => form.submit());
    });
  });
})();
